import React from 'react';
import { Globe, Zap, Languages, Shield, Mic, Laptop } from 'lucide-react';
import {useTranslation} from "react-i18next";

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

export const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => {
  return (
    <div className="group relative bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
      <div className="relative z-10">
        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center text-white mb-6">
          {icon}
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
        <p className="text-gray-600 leading-relaxed">{description}</p>
      </div>
    </div>
  );
};

export const Features: React.FC = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: <Globe className="h-6 w-6" />,
      title: t('features.tags.globalMinistry.title'),
      description: t('features.tags.globalMinistry.description')
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: t('features.tags.realTime.title'),
      description: t('features.tags.realTime.description')
    },
    {
      icon: <Languages className="h-6 w-6" />,
      title: t('features.tags.multiLang.title'),
      description: t('features.tags.multiLang.description')
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: t('features.tags.privacy.title'),
      description: t('features.tags.privacy.description')
    },
    {
      icon: <Mic className="h-6 w-6" />,
      title: t('features.tags.voice.title'),
      description: t('features.tags.voice.description')
    },
    {
      icon: <Laptop className="h-6 w-6" />,
      title: t('features.tags.setup.title'),
      description: t('features.tags.setup.description')
    }
  ];

  return (
    <section id="features" className="py-20 md:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('features.title')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('features.subTitle')}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
